import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Edit, Trash2, Plus, ArrowLeft } from "react-feather";
import StatusBadge from "../../../components/common/StatusBadge";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import PartyPicker from "../../stock/components/PartyPicker";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useCanManageAmcEquipment, useCanManageAmcContracts } from "../useAmcPermissions";
import WarrantyBadge from "../components/WarrantyBadge";
import AddEquipmentModal from "../components/AddEquipmentModal";
import EditEquipmentModal from "../components/EditEquipmentModal";
import AmcContractSection from "../components/AmcContractSection";
import AmcContractList from "../components/AmcContractList";
import AmcScheduleSection from "../components/AmcScheduleSection";
import {
  useGetCustomerEquipmentQuery,
  useDeleteCustomerEquipmentMutation,
  useGetAmcContractsQuery,
  useGetAmcContractEquipmentQuery,
  useSetAmcContractEquipmentMutation,
  useSetAmcContractStatusMutation,
  useSendAmcRenewalReminderMutation,
} from "../amcApi";

const EQUIPMENT_STATUS_CLASSES = { ACTIVE: "badge-linesuccess", REMOVED: "badge-linedark", REPLACED: "badge-linewarning" };
const EQUIPMENT_STATUS_LABELS = { ACTIVE: "In service", REMOVED: "Removed", REPLACED: "Replaced" };

const fmtDate = (v) => (v ? String(v).substring(0, 10) : "—");

/**
 * AMC - one page for the whole thing.
 *
 * Built this way on direct instruction after the first cut was rejected as too many steps:
 * "too many options to configure will make the user reject the module". So the contract, its
 * covered equipment and its visit schedule all live here, and a contract saves from five
 * fields without touching equipment at all.
 *
 * Equipment is an optional enrichment, never a prerequisite. Tick an item to bring it under
 * the selected contract; leave everything unticked and the contract still works.
 *
 * 2026-09-08: the page used to open on "Choose a customer to start" and show nothing else,
 * so an AMC could only be found by someone who already knew whose it was - there was no way
 * to ask what the company actually has on contract. It now opens on the full register, and
 * the customer picker filters that list instead of gating it. Renewal was added at the same
 * time: a contract stays ACTIVE past its end date, so an unrenewed one goes quiet rather
 * than announcing itself, and until now nothing linked a new contract to the one it
 * replaced even though the schema has carried that link since Batch136.
 */
const NewAmcPage = () => {
  const canManageEquipment = useCanManageAmcEquipment();
  const canManageContracts = useCanManageAmcContracts();
  const { selectedBranchId } = useBranchFilter();

  // Two different things that used to share one state (2026-09-11):
  //  - filterCustomer: what the user picked in the Customer box - narrows the register.
  //  - customer: whose contract/equipment panels are open below.
  // Opening a row used to set the one shared value, which silently narrowed the
  // register to that customer; "Back to AMC Records" then only closed the
  // contract, so the user was left on a one-customer list with an empty "New AMC"
  // form - the reported "back doesn't go to the AMC list". Opening a row now
  // changes only `customer`, and Back restores `customer` to whatever the filter is.
  const [filterCustomer, setFilterCustomer] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [contractGuid, setContractGuid] = useState(null);
  const [renewFrom, setRenewFrom] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const contractPanelRef = useRef(null);

  const branchId = selectedBranchId ?? customer?.branchId;

  // The customer filter keeps to the header branch (2026-10-02): its search is that
  // branch's customers, and a filter customer from another branch is dropped when
  // the header changes, so the list does not go empty for no visible reason.
  useEffect(() => {
    if (selectedBranchId != null && filterCustomer && filterCustomer.branchId !== selectedBranchId) pickCustomer(null);
  }, [selectedBranchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The whole register, branch-scoped by the header picker. Requested by name: "show all
  // amc records by default and then filter for customers when data exists" - the customer
  // picker below is now a filter over this, not a gate in front of it.
  const { data: allContracts = [], isLoading: loadingContracts } = useGetAmcContractsQuery(
    { branchId: selectedBranchId ?? undefined },
    { skip: !canManageContracts }
  );

  const { data: contracts = [] } = useGetAmcContractsQuery(
    { customerBranchId: customer?.branchId, custId: customer?.custId },
    { skip: !customer || !canManageContracts }
  );

  const listRows = filterCustomer
    ? allContracts.filter((c) => c.custId === filterCustomer.custId && c.customerBranchId === filterCustomer.branchId)
    : allContracts;

  // Opening a row is the same state the customer picker produces, so everything below
  // behaves identically however the contract was reached.
  //
  // The panels it opens sit BELOW the register, which on a full list is off the bottom
  // of the screen - clicking Open/Edit looked like it had done nothing at all ("loads
  // the same page"). It is one page by design, so the fix is to take the user to the
  // part of it that just appeared rather than to invent a route.
  const openContract = (row) => {
    setCustomer({ custId: row.custId, custName: row.customerName, branchId: row.customerBranchId });
    setContractGuid(row.recordGuid);
    setRenewFrom(null);
    // Next paint: the panel does not exist yet on the click that opens it.
    window.requestAnimationFrame(() =>
      contractPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  };

  // ?contract=<guid> opens the page straight on that contract - what the dashboard's AMC
  // cards link to. The param is consumed once and dropped from the URL so that closing
  // the contract with "Back to AMC Records" does not immediately re-open it.
  //
  // The register is branch-scoped by the header filter, so a contract from another branch
  // genuinely is not in `allContracts`; say so rather than silently doing nothing.
  const requestedContract = searchParams.get("contract");
  useEffect(() => {
    if (!requestedContract || loadingContracts) return;
    const row = allContracts.find((c) => c.recordGuid === requestedContract);
    if (row) openContract(row);
    else toast.error("That AMC is not in the current branch - switch the branch filter to All branches.");
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedContract, loadingContracts, allContracts]);

  // Deselects whatever is open and puts the page back to the register, exactly
  // as the user had it: the customer filter they chose (if any) is kept, the
  // panels a row-open brought up close, and the view returns to the list.
  const backToRegister = () => {
    setContractGuid(null);
    setRenewFrom(null);
    setCustomer(filterCustomer);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  // "Panels are open that the filter didn't ask for": a contract, a renewal
  // draft, OR a customer brought up by opening a row. The last case matters -
  // cancelling a renewal, or picking "+ New AMC" in the contract dropdown,
  // clears the contract/renewal but leaves that row's customer on screen, and
  // without it the Back link vanished and the user was stranded on an empty
  // "New AMC" form. `customer === filterCustomer` (same object, set by
  // pickCustomer/backToRegister) is the one state that IS the plain register.
  const isPanelOpen = Boolean(contractGuid || renewFrom || (customer && customer !== filterCustomer));

  // Renewing is a NEW contract carrying on from this one's end date, linked back to it -
  // never an edit of the old one, whose visit history has to stay exactly as it was.
  const startRenewal = (row) => {
    setCustomer({ custId: row.custId, custName: row.customerName, branchId: row.customerBranchId });
    setContractGuid(null);
    setRenewFrom(row);
    window.requestAnimationFrame(() =>
      contractPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  };

  const { data: equipment = [], isLoading: loadingEquipment } = useGetCustomerEquipmentQuery(
    { customerBranchId: customer?.branchId, custId: customer?.custId, status: "ACTIVE" },
    { skip: !customer || !canManageEquipment }
  );

  const { data: coveredEquipment = [] } = useGetAmcContractEquipmentQuery(contractGuid, {
    skip: !contractGuid,
  });

  const [setContractEquipment, { isLoading: isSavingCoverage }] = useSetAmcContractEquipmentMutation();
  const [deleteEquipment] = useDeleteCustomerEquipmentMutation();
  const [setContractStatus] = useSetAmcContractStatusMutation();
  const [sendReminder] = useSendAmcRenewalReminderMutation();

  // Changing the customer FILTER must not leave the previous customer's contract selected.
  // Done here rather than in an effect on `customer`, because opening or renewing a row from
  // the list sets the customer and the contract together and an effect would undo that.
  const pickCustomer = (next) => {
    setFilterCustomer(next);
    setCustomer(next);
    setContractGuid(null);
    setRenewFrom(null);
  };

  const coveredIds = new Set(coveredEquipment.map((e) => e.equipmentId));

  const toggleCovered = async (equipmentId) => {
    if (!contractGuid) {
      toast.error("Create or select an AMC first, then tick what it covers.");
      return;
    }
    const next = new Set(coveredIds);
    if (next.has(equipmentId)) next.delete(equipmentId);
    else next.add(equipmentId);

    try {
      await setContractEquipment({ recordGuid: contractGuid, equipmentIds: [...next] }).unwrap();
    } catch (err) {
      toast.error(err?.data?.message || "Could not update coverage.");
    }
  };

  const handleDeleteEquipment = async (row) => {
    const confirmed = await confirmDelete({
      title: `Remove ${row.productName}?`,
      text: `It will be taken off ${row.customerName}'s equipment. Service history is not affected.`,
    });
    if (!confirmed) return;
    try {
      await deleteEquipment({ recordGuid: row.recordGuid, reason: "Removed from the AMC screen" }).unwrap();
      toast.success("Equipment removed.");
    } catch (err) {
      toast.error(err?.data?.message || "Could not remove that item.");
    }
  };

  if (!canManageContracts && !canManageEquipment) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-warning">You do not have access to AMC &amp; Warranty.</div>
        </div>
      </div>
    );
  }

  // USER-TRIGGERED ONLY - there is no scheduler anywhere in this feature. The
  // reminder goes out because somebody clicked the icon on this row.
  //
  // The server attempts and logs each channel independently, so the result can
  // be a half-success; report per channel rather than a blanket "sent", and
  // treat SKIPPED (no address/number on file) as distinct from FAILED so the
  // user knows whether to fix the customer record or the branch settings.
  const handleSendReminder = async (row, channels) => {
    const label = channels[0] === "EMAIL" ? "email" : "WhatsApp";
    if (!window.confirm(`Send a renewal reminder to ${row.customerName} by ${label}?`)) return;
    try {
      const log = await sendReminder({ recordGuid: row.recordGuid, channels }).unwrap();
      const latest = (log || []).find((e) => channels.includes(e.channel));
      if (!latest) toast.success("Reminder processed.");
      else if (latest.status === "SENT") toast.success(`Reminder sent by ${label} to ${latest.sentTo}.`);
      else if (latest.status === "SKIPPED") toast.error(latest.errorMessage || `No ${label} contact on file for this customer.`);
      else toast.error(`${label} reminder failed: ${latest.errorMessage || "unknown error"}`);
    } catch (err) {
      toast.error(err?.message || err?.data?.message || "Could not send the reminder.");
    }
  };

  // Cancel stops the contract being counted in the service-due and renewals-due
  // lists (both filter Status='ACTIVE' server-side) while leaving it on this
  // list under a CANCELLED badge - "not counted anywhere, but shows as
  // cancelled". Reversible, so a mis-click is not permanent.
  //
  // Hold / Release (Batch217) go through the same endpoint: ON_HOLD needs a reason and a
  // balance outstanding (the server checks both); RELEASE is sent as ACTIVE.
  const handleSetContractStatus = async (row, action) => {
    let reason = null;
    if (action === "CANCELLED" || action === "ON_HOLD") {
      reason = window.prompt(
        action === "CANCELLED"
          ? `Cancel ${row.contractNo}? Give a reason - it is kept on the contract.`
          : `Put ${row.contractNo} on hold for non-payment? Its service visits stop showing as due and the service log is locked until it is released. Give a reason.`
      );
      // Cancel on the prompt (null) aborts; an empty string would be refused by
      // the server anyway, so stop here either way.
      if (reason === null || !reason.trim()) return;
    } else if (
      !window.confirm(
        action === "RELEASE"
          ? `Release ${row.contractNo} from hold? Its due visits will start counting again.`
          : `Reactivate ${row.contractNo}? Its due visits will start counting again.`
      )
    ) {
      return;
    }
    const status = action === "RELEASE" ? "ACTIVE" : action;
    const done = { CANCELLED: "AMC cancelled.", ON_HOLD: "AMC put on hold.", RELEASE: "AMC released from hold." };
    try {
      await setContractStatus({ recordGuid: row.recordGuid, status, reason: reason?.trim() || null }).unwrap();
      toast.success(done[action] || "AMC reactivated.");
    } catch (err) {
      toast.error(err?.message || err?.data?.message || "Could not change the AMC status.");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            {/* This is a top-level page, so there is no parent screen to go back
                TO - but once a contract (or a renewal draft) is open, the panels
                below take over the page and there was no way out of them except
                the browser's own Back. This clears the selection and returns the
                page to just the register. */}
            {isPanelOpen && (
              <button
                type="button"
                className="btn btn-link p-0 mb-2 d-inline-flex align-items-center"
                onClick={backToRegister}
              >
                <ArrowLeft size={16} className="me-1" /> Back to AMC Records
              </button>
            )}
            <h4>AMC &amp; Warranty</h4>
            <h6 className="text-muted">
              Every AMC on record — open one to see its equipment and service schedule, or pick a customer to
              add a new one
            </h6>
          </div>
        </div>

        {/* ---- 1. who (a filter now, not a gate) ---- */}
        <div className="card">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-5">
                <label className="form-label">Customer</label>
                <PartyPicker
                  key={selectedBranchId ?? "all"}
                  mode={selectedBranchId != null ? "branch" : "all-branches"}
                  branchId={selectedBranchId ?? undefined}
                  value={
                    filterCustomer
                      ? { value: filterCustomer.custId, label: filterCustomer.custName, customer: filterCustomer }
                      : null
                  }
                  onChange={(opt) => pickCustomer(opt?.customer || null)}
                />
                <small className="text-muted">
                  Filters the list below. Choosing one is only required to create a new AMC.
                </small>
              </div>
              {filterCustomer && (
                <div className="col-md-7">
                  <p className="mb-0 text-muted">
                    {contracts.length === 0
                      ? "No AMC on record for this customer yet."
                      : `${contracts.length} AMC${contracts.length === 1 ? "" : "s"} on record.`}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ---- 2. every AMC on record ---- */}
        {canManageContracts && (
          <AmcContractList
            rows={listRows}
            isLoading={loadingContracts}
            selectedGuid={contractGuid}
            onOpen={openContract}
            onRenew={startRenewal}
            onSetStatus={handleSetContractStatus}
            onSendReminder={handleSendReminder}
            canManage={canManageContracts}
            customerFiltered={Boolean(filterCustomer)}
          />
        )}

        {/* ---- 3. the contract ---- */}
        <div ref={contractPanelRef} style={{ scrollMarginTop: 90 }} />
        {/* Opening a row scrolls the page down to here, so the top-of-page Back
            link is out of sight - repeat it where the user actually is. */}
        {isPanelOpen && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center"
              onClick={backToRegister}
            >
              <ArrowLeft size={14} className="me-1" /> Back to AMC Records
            </button>
            <small className="text-muted">
              {renewFrom ? `Renewing ${renewFrom.contractNo}` : contractGuid ? "Viewing" : "New AMC for"} · {customer?.custName}
            </small>
          </div>
        )}
        {customer && canManageContracts && (
          <AmcContractSection
            customer={customer}
            branchId={branchId}
            contracts={contracts}
            selectedGuid={contractGuid}
            onSelect={(guid) => {
              setContractGuid(guid);
              setRenewFrom(null);
            }}
            renewFrom={renewFrom}
            // Cancel on a renewal draft means "not now" - go back to the list the
            // Renew button was pressed on, not to a blank form for that customer.
            onCancelRenewal={backToRegister}
            canManage={canManageContracts}
          />
        )}

        {/* ---- 4. equipment, optional ---- */}
        {customer && canManageEquipment && (
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                  <h5 className="mb-0">
                    Equipment &amp; Warranty <span className="text-muted fw-normal">— optional</span>
                  </h5>
                  <small className="text-muted">
                    {contractGuid
                      ? "Tick what this AMC covers. Warranty is tracked whether or not it is covered."
                      : "Warranty is tracked here. Create or select an AMC above to mark what it covers."}
                  </small>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                  onClick={() => setAddOpen(true)}
                >
                  <Plus size={16} /> Add Equipment
                </button>
              </div>

              {loadingEquipment && <p className="text-muted mb-0">Loading…</p>}

              {!loadingEquipment && equipment.length === 0 && (
                <p className="text-muted mb-0">
                  Nothing recorded yet. Add it from a Stock Out, a quotation prototype, or the product list —
                  or skip it entirely, the AMC works without it.
                </p>
              )}

              {equipment.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        {contractGuid && <th style={{ width: 70 }}>Covered</th>}
                        <th>Equipment</th>
                        <th>Installed</th>
                        <th>Warranty</th>
                        <th>Status</th>
                        <th>Source</th>
                        <th className="text-end">&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody>
                      {equipment.map((r) => (
                        <tr key={r.recordGuid}>
                          {contractGuid && (
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={coveredIds.has(r.equipmentId ?? -1) || coveredEquipment.some((c) => c.equipmentRecordGuid === r.recordGuid)}
                                onChange={() => {
                                  const match = coveredEquipment.find((c) => c.equipmentRecordGuid === r.recordGuid);
                                  toggleCovered(match ? match.equipmentId : r.equipmentId);
                                }}
                                disabled={isSavingCoverage || !r.equipmentId}
                                aria-label={`Cover ${r.productName}`}
                              />
                            </td>
                          )}
                          <td>
                            <span className="fw-bold">{r.productName}</span>
                            {Number(r.quantity) !== 1 && <span className="text-muted"> × {Number(r.quantity)}</span>}
                            {r.serialNumber && <small className="d-block text-muted">S/N {r.serialNumber}</small>}
                          </td>
                          <td>{fmtDate(r.installationDate)}</td>
                          <td>
                            <WarrantyBadge status={r.warrantyStatus} daysRemaining={r.warrantyDaysRemaining} />
                            {r.warrantyEndDate && (
                              <small className="d-block text-muted">to {fmtDate(r.warrantyEndDate)}</small>
                            )}
                          </td>
                          <td>
                            <StatusBadge status={r.status} classMap={EQUIPMENT_STATUS_CLASSES} labelMap={EQUIPMENT_STATUS_LABELS} />
                          </td>
                          <td>
                            <small className="text-muted">
                              {r.sourceStockOutNo || (r.sourcePrototypeName ? `Prototype: ${r.sourcePrototypeName}` : "Entered manually")}
                            </small>
                          </td>
                          <td className="text-end">
                            <div className="d-flex gap-2 justify-content-end">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setEditItem(r)}
                                aria-label={`Edit ${r.productName}`}
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteEquipment(r)}
                                aria-label={`Remove ${r.productName}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---- 5. the schedule ---- */}
        {contractGuid && (
          <AmcScheduleSection
            contractRecordGuid={contractGuid}
            canManage={canManageContracts}
            onHold={[...contracts, ...allContracts].some((c) => c.recordGuid === contractGuid && c.status === "ON_HOLD")}
          />
        )}
      </div>

      <AddEquipmentModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        customer={customer}
        branchId={branchId}
        locationId={null}
      />

      <EditEquipmentModal open={!!editItem} onClose={() => setEditItem(null)} item={editItem} />
    </div>
  );
};

export default NewAmcPage;
